import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  afterNextRender,
  computed,
  effect,
  inject,
  signal,
  untracked,
  viewChild,
  Injector,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { first } from 'rxjs';

import { TippyDirective } from '../../../../shared/directives/tippy.directive';

import { TutorialRunnerService } from '../../engine/tutorial-runner.service';
import { TutorialDefinition, TutorialPrerequisite } from '../../models/onboarding.types';
import { OnboardingStore } from '../../store/onboarding.store';
import { OnboardingPanelComponent } from '../onboarding-panel/onboarding-panel.component';
import { openPrerequisitesDialog } from '../onboarding-prerequisites-dialog/onboarding-prerequisites-dialog.component';
import { OnboardingTutorialHostComponent } from '../onboarding-tutorial-host/onboarding-tutorial-host.component';
import {
  OnboardingWelcomeDialogComponent,
  WelcomeDialogResult,
} from '../onboarding-welcome-dialog/onboarding-welcome-dialog.component';

export const LAUNCHER_COLLAPSED_STORAGE_KEY = 'onboarding-launcher-collapsed';
const RING_CIRCUMFERENCE = 2 * Math.PI * 16;

/** Preferência visual não autoritativa (só o formato do botão); falha de storage é ignorada. */
function readCollapsedPreference(): boolean {
  try {
    return globalThis.localStorage?.getItem(LAUNCHER_COLLAPSED_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function writeCollapsedPreference(collapsed: boolean): void {
  try {
    globalThis.localStorage?.setItem(LAUNCHER_COLLAPSED_STORAGE_KEY, collapsed ? '1' : '0');
  } catch {
    // navegação privada / storage bloqueado: segue sem lembrar a preferência
  }
}

/**
 * Ponto de montagem do onboarding no layout privado: botão flutuante, painel, boas-vindas e o
 * host do tour (Driver.js só é carregado quando um tutorial começa).
 */
@Component({
  selector: 'app-onboarding-shell',
  standalone: true,
  imports: [MatIconModule, TranslateModule, TippyDirective, OnboardingPanelComponent, OnboardingTutorialHostComponent],
  templateUrl: './onboarding-shell.component.html',
  styleUrl: './onboarding-shell.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OnboardingShellComponent {
  protected readonly store = inject(OnboardingStore);
  protected readonly runner = inject(TutorialRunnerService);
  private readonly dialog = inject(MatDialog);
  private readonly injector = inject(Injector);

  private readonly launcherButton = viewChild<ElementRef<HTMLButtonElement>>('launcherButton');

  readonly collapsed = signal(readCollapsedPreference());
  readonly ringCircumference = RING_CIRCUMFERENCE;
  readonly ringDash = computed(() => `${(this.store.progressPercent() / 100) * RING_CIRCUMFERENCE} ${RING_CIRCUMFERENCE}`);
  readonly showLauncher = computed(
    () => this.store.showLauncher() && !this.store.panelOpen() && !this.runner.hasActiveTutorial()
  );

  /** Texto do botão: deixa explícito que o número são etapas obrigatórias pendentes. */
  readonly progressKey = computed(() => {
    const pending = this.store.pendingRequired();
    if (pending === 0) {
      return 'onboarding.launcher.progress';
    }
    return pending === 1 ? 'onboarding.launcher.pending_one' : 'onboarding.launcher.pending_other';
  });
  readonly progressParams = computed(() => ({
    done: this.store.state()?.requiredCompleted,
    total: this.store.state()?.requiredTotal,
    pending: this.store.pendingRequired(),
  }));
  /** Linha do botão expandido: lembrete pós-jornada (ex.: perfil incompleto) ou progresso. */
  readonly metaKey = computed(() => this.store.reminder()?.labelKey ?? this.progressKey());
  readonly metaParams = computed<Record<string, number | undefined>>(
    () => this.store.reminder()?.params ?? this.progressParams()
  );

  constructor() {
    this.store.initialize();
    this.runner.onTutorialBlocked((tutorial, missing) => this.openPrerequisites(tutorial, missing));
    inject(DestroyRef).onDestroy(() => this.store.reset());

    effect(() => {
      if (this.store.welcomeRequested()) {
        untracked(() => this.openWelcome());
      }
    });
  }

  openPanel(): void {
    this.store.openPanel();
  }

  toggleCollapsed(): void {
    const next = !this.collapsed();
    this.collapsed.set(next);
    writeCollapsedPreference(next);
  }

  /** Painel fechado: o foco volta para o botão flutuante. */
  onPanelClosed(): void {
    afterNextRender(() => this.launcherButton()?.nativeElement.focus(), { injector: this.injector });
  }

  /** Tutorial bloqueado por falta de dado: mostra o que falta e, se escolhido, abre o tutorial que resolve. */
  private openPrerequisites(tutorial: TutorialDefinition, missing: TutorialPrerequisite[]): void {
    openPrerequisitesDialog(this.dialog, { tutorial, missing })
      .pipe(first())
      .subscribe((result) => {
        if (result?.startTutorial) {
          this.runner.start(result.startTutorial, 'catalog');
        }
      });
  }

  private openWelcome(): void {
    this.store.consumeWelcomeRequest();
    this.dialog
      .open<OnboardingWelcomeDialogComponent, void, WelcomeDialogResult>(OnboardingWelcomeDialogComponent, {
        maxWidth: '30rem',
        width: '100%',
        maxHeight: '90dvh',
        panelClass: 'onboarding-welcome-dialog-panel',
        ariaLabelledBy: 'onboarding-welcome-title',
        // só sai pelos botões: clicar fora, Esc ou um redirect após o login não podem sumir com
        // as boas-vindas sem deixar a jornada iniciada ou pausada (com o botão flutuante visível)
        disableClose: true,
        closeOnNavigation: false,
      })
      .afterClosed()
      .pipe(first())
      .subscribe((result) => {
        if (result === 'start') {
          this.store.start();
        } else if (result === 'later') {
          this.store.pause();
        }
        // fechado por código (ex.: logout): nada é gravado e as boas-vindas voltam no próximo acesso
      });
  }
}
